import * as XLSX from "xlsx";

interface ExportSheetOptions {
    sheetName?: string;
    sheetData: Record<string, any>[] | any[][];
    sheetFilter?: string[];
    sheetHeader?: any[];
    columnWidths?: number[];
}

interface ExportExcelOptions {
    fileName?: string;
    datas: ExportSheetOptions[];
    saveAsBlob?: boolean;
}

function getRows(sheetData: Record<string, any>[] | any[][], sheetFilter?: string[]): any[][] {
    if (!Array.isArray(sheetData)) {
        return [];
    }

    return sheetData.map((row: Record<string, any> | any[]) => {
        const keys = sheetFilter?.length ? sheetFilter : Object.keys(row);
        return keys.map((key) => row[key as keyof typeof row]);
    });
}

function getExcelDateValue(value: Date, date1904?: boolean): number {
    const epoch = Date.parse(value.toString());
    return (epoch - new Date(Date.UTC(1899, 11, 30)).getTime()) / (24 * 60 * 60 * 1000) + (date1904 ? 1462 : 0);
}

function createWorksheet(data: any[][]): XLSX.WorkSheet {
    const worksheet: XLSX.WorkSheet = {};
    const range = {
        s: { c: 10000000, r: 10000000 },
        e: { c: 0, r: 0 },
    };

    data.forEach((row, rowIndex) => {
        row.forEach((value, columnIndex) => {
            if (range.s.r > rowIndex) range.s.r = rowIndex;
            if (range.s.c > columnIndex) range.s.c = columnIndex;
            if (range.e.r < rowIndex) range.e.r = rowIndex;
            if (range.e.c < columnIndex) range.e.c = columnIndex;

            if (value == null) {
                return;
            }

            const cell: XLSX.CellObject = { t: "s", v: value };

            if (typeof value === "number") {
                cell.t = "n";
            } else if (typeof value === "boolean") {
                cell.t = "b";
            } else if (value instanceof Date) {
                cell.t = "n";
                cell.z = (XLSX.SSF as any)._table[14];
                cell.v = getExcelDateValue(value);
            } else if (typeof value === "string" && /^hyperlink:/.test(value)) {
                const target = value.replace(/^hyperlink:/, "");
                cell.l = { Target: target };
                cell.v = target;
                cell.t = "s";
            } else {
                cell.t = "s";
            }

            worksheet[XLSX.utils.encode_cell({ c: columnIndex, r: rowIndex })] = cell;
        });
    });

    if (range.s.c < 10000000) {
        worksheet["!ref"] = XLSX.utils.encode_range(range);
    }

    return worksheet;
}

export default class ExportJsonExcel {
    private readonly options: ExportExcelOptions;

    constructor(options: ExportExcelOptions) {
        this.options = options;
    }

    buildWorkbook(): XLSX.WorkBook {
        const workbook = XLSX.utils.book_new();

        this.options.datas.forEach((data, index) => {
            const rows = getRows(data.sheetData, data.sheetFilter);
            const sheetRows = data.sheetHeader ? [data.sheetHeader, ...rows] : rows;
            const worksheet = createWorksheet(sheetRows);

            if (data.columnWidths?.length) {
                worksheet["!cols"] = data.columnWidths.map((width) => ({
                    width: width ? parseInt(String(256 * (Number(width) / 100)), 10) : 20,
                }));
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, data.sheetName || `sheet${index + 1}`);
        });

        return workbook;
    }

    saveExcel(): File | void {
        const workbook = this.buildWorkbook();

        const fileName = `${this.options.fileName || "download"}.xlsx`;

        if (this.options.saveAsBlob) {
            const output = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            return new File([output], fileName, {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                lastModified: Date.now(),
            });
        }

        XLSX.writeFile(workbook, fileName);
    }
}